"use client";

import { Skeleton } from "design-system/skeleton";
import dynamic from "next/dynamic";
import { LazyLoadDialog } from "@/components/custom/lazy-load-component";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { useAccountEntitlementOptions } from "@/subsystems/transfer/accounts/hooks/use-account-entitlement-options";
import type { BlockchainAddressesFormSchema, CustomerAddress } from "@/subsystems/transfer/accounts/types";
import { getChainsIdsHaveAdded } from "@/subsystems/transfer/accounts/utils/customer-address-utils";

export type BlockchainAddressAddNetworkFormDialogData = {
  currentData?: CustomerAddress | null;
};

export function BlockchainAddressAddNetworkFormDialog({
  dialogControls,
}: {
  dialogControls: UseDialogReturn<BlockchainAddressAddNetworkFormDialogData>;
}) {
  const currentData = dialogControls.data?.currentData;
  const { address: networkAddress, nickname } = currentData || {};
  const { showMintIL } = useAccountEntitlementOptions();

  const chainIdsAlreadyAdded = currentData ? getChainsIdsHaveAdded(currentData, !showMintIL) : [];
  const networksAlreadyAddedCheckboxes = chainIdsAlreadyAdded.map(String);

  const defaultValues = {
    networkAddress: networkAddress ?? "",
    nickname: nickname ?? "",
  } as BlockchainAddressesFormSchema;

  return (
    <LazyLoadDialog
      description={
        showMintIL
          ? "Networks can only be added using their network address. Adding one will initiate either a scheduled settlement, an instant settlement, or both if available."
          : "Networks can only be added using their network address. Adding one tracks rewards on that network once it is approved."
      }
      dialogControls={dialogControls}
      title="Add Network"
    >
      <BlockchainAddressesAddForm
        key={currentData?.id}
        addressId={currentData?.id}
        defaultValues={defaultValues}
        dialogControls={dialogControls}
        disabledNetworks={networksAlreadyAddedCheckboxes}
        hiddenNetworks={networksAlreadyAddedCheckboxes}
        hideDisabledNetworks
        intendedMutation="adding-network"
        onSuccess={() => dialogControls?.dismiss()}
        redirectOnSuccess={false}
        variant="add-network-only"
      />
    </LazyLoadDialog>
  );
}

const BlockchainAddressesAddForm = dynamic(
  () =>
    import("@/subsystems/transfer/accounts/pages/blockchain-addresses-add-form").then(
      (mod) => mod.BlockchainAddressesAddForm,
    ),
  {
    loading: () => (
      <div className="w-full space-y-6">
        {/* Network Section */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
            <Skeleton className="h-13 rounded-md" />
          </div>
        </div>

        {/* Buttons Section */}
        <div className="flex justify-between">
          <Skeleton className="h-10 w-40 rounded-md" />
          <Skeleton className="h-10 w-40 rounded-md" />
        </div>
      </div>
    ),
    ssr: false,
  },
);
